import React, { useEffect } from 'react';
import * as Yup from 'yup';
import PropTypes from 'prop-types';
import { withFormik } from 'formik';
import { useIntl } from 'react-intl';
import { get } from 'lodash';
import styled from 'styled-components';

import { useSelector } from 'react-redux';

import { applicationSelectors } from '../../../redux/selectors';
import { useCustomLayout, useIsMobileLayout } from '../../../hooks';
import { RoleSelect } from '../../../assets/images';

import Box from '../../../components/Styled/Box';
import Select from '../../../components/Inputs/Select';
import Button from '../../../components/Buttons/Button';
import Typography from '../../../components/Typography/Typography';

const Arrow = styled.div`
  position: absolute;
  top: -10px;
  left: calc( 50% - 8px );
  width: 0; 
  height: 0; 
  border-left: 8px solid transparent;
  border-right: 8px solid transparent;
  border-bottom: 10px solid ${({ theme }) => theme.colors.coldgrey300};
`;

const RoleInfo = ({ text, ...boxProps }) => (
  <Box
    position='relative'
    backgroundColor='coldgrey300'
    borderRadius='5px'
    {...boxProps}
  >
    <Arrow />
    <Box
      p='16px 21px'
      mt='16px'
      style={{ textAlign: 'center' }}
    >
      <Typography variant='helper'>{text}</Typography>
    </Box>
  </Box>
);

RoleInfo.propTypes = {
  text: PropTypes.string.isRequired,
};

const validationSchema = (f) => Yup.object().shape({
  role: Yup.object().nullable().required(f({ id: 'errors.required.simple' })),
});

const RoleForm = (props) => {
  const {
    values,
    touched,
    errors,
    isValid,
    handleBlur,
    handleSubmit,
    isSubmitting,
    setFieldValue,
  } = props;

  const { formatMessage: f } = useIntl();
  const isMobile = useIsMobileLayout();

  useCustomLayout({
    color: true,
    image: RoleSelect,
    hubSpotCss: 'top: 0px; left: 0px;',
  });

  const currentApplication = useSelector(applicationSelectors.currentApplication);

  const roleOptions = get(currentApplication, 'applications_questionnaires', []).map((item) => ({
    id: item.role,
    name: item.role_name || item.role,
    ...item,
  }));

  const disabledButton = !isValid || isSubmitting;

  useEffect(() => {
    if (roleOptions.length === 1 && !values.role) {
      setFieldValue('role', roleOptions[0]);
    }
  }, [roleOptions, setFieldValue, values.role]);

  return (
    <form onSubmit={handleSubmit}>
      <Select
        label={f({ id: 'words.your_role' })}
        placeholder={f({ id: 'select.placeholder' })}
        options={roleOptions}
        name='role'
        value={values.role}
        onChange={(val) => setFieldValue('role', val)}
        onBlur={handleBlur('role')}
        error={touched.role ? errors.role : ''}
      />

      {get(values, 'role.role_description', '') && <RoleInfo text={values.role.role_description} />}

      <Box mt='32px'>
        <Button
          width={isMobile ? '100%' : ''}
          centerText={isMobile}
          type='submit'
          disabled={disabledButton}
          rightIcon={{ icon: 'arrow-right' }}
        >
          {f({ id: 'words.continue' })}
        </Button>
      </Box>
    </form>
  );
};

const EnhancedRoleForm = withFormik({
  mapPropsToValues: () => ({
    role: null,
    legal_agreement: false,
    not_a_robot: false,
  }),
  validationSchema: ({ f }) => validationSchema(f),
  handleSubmit: (values, { props }) => props.onSubmit(values),
  displayName: 'RoleForm',
})(RoleForm);

RoleForm.propTypes = {
  values: PropTypes.object.isRequired,
  touched: PropTypes.object.isRequired,
  errors: PropTypes.object.isRequired,
  isValid: PropTypes.bool.isRequired,
  handleBlur: PropTypes.func.isRequired,
  handleSubmit: PropTypes.func.isRequired,
  isSubmitting: PropTypes.bool.isRequired,
  setFieldValue: PropTypes.func.isRequired,
};

export default EnhancedRoleForm;
